import { useEffect, useState } from "react";
import PulseLoader from "react-spinners/PulseLoader";
import { Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  formatCalendarDate,
  formatDate,
  getToken,
  setCaseId,
  setClientId,
} from "../../../Utils/helper";
import { api_without_cancellation } from "../../../api/api";
import { fetchCaseSummary, fetchHeaderSummary } from "../../../api/case";
import {
  fetchAllPages,
  fetchCurrentCase,
  setCaseSummary,
} from "../../../Redux/caseData/caseDataSlice";
import { setHeaderSummary } from "../../../Redux/header/headerDataSlice";
import { setHeaderName } from "../../../Redux/header_name/action";
import incidentIcon from "../../../assets/images/incident.svg";
import EditCaseTypeDateModal from "../../EditCaseTypeDateModal";
import avatarImage from "../../../assets/images/avatar.svg";
import UserChatBubble from "../../Forms/FirmUserBtn";
import ChatSendingPopup from "../../Forms/common/ChatSendingPopup";

import "./intake-reporting.css";

const IntakeReporting = () => {
  const origin = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";

  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState([]);
  const [chatData, setChatData] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [isEnteringCase, setIsEnteringCase] = useState(false);
  const [enterCaseError, setEnterCaseError] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userOnlineStatus = useSelector((state) => state.chat2?.userOnlineStatus ?? {});

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${origin}/api/get_intake_reporting/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }
      const data = await response.json();
      setCases(data?.cases ?? []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const renderValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return <span className="text-primary-25">-</span>;
    }
    return value;
  };

  const renderDate = (caseData, field) => (
    <button
      type="button"
      className="intake-report-date"
      aria-label={`View case type and dates for ${caseData.client_name || "client"}: ${field.replaceAll("_", " ")}`}
    >
      {field === "incident_date" && <img src={incidentIcon} alt="" className="ic ic-15 m-r-5" />}
      {renderValue(
        caseData[field]
          ? field === "incident_date"
            ? formatCalendarDate(caseData[field])
            : formatDate(caseData[field])
          : ""
      )}
    </button>
  );

  const enterCase = async () => {
    if (isEnteringCase || !selectedCase?.client_id || !selectedCase?.case_id) return;
    const { client_id: clientId, case_id: caseId } = selectedCase;
    setIsEnteringCase(true);
    setEnterCaseError("");
    try {
      await api_without_cancellation.get(`/api/switch_client/${clientId}/${caseId}/1/`);
      setClientId(clientId);
      setCaseId(caseId);
      const [currentCase, headerSummary, caseSummary, pages] = await Promise.all([
        dispatch(fetchCurrentCase(clientId, caseId)),
        fetchHeaderSummary(clientId, caseId),
        fetchCaseSummary(clientId, caseId),
        dispatch(fetchAllPages(caseId, true)),
      ]);
      if (currentCase === undefined || pages === undefined) {
        throw new Error("Failed to load the selected case.");
      }
      dispatch(setHeaderSummary(headerSummary));
      dispatch(setCaseSummary(caseSummary));
      dispatch(setHeaderName("Case"));
      setSelectedCase(null);
      navigate(`/bp-case/${clientId}/${caseId}`);
    } catch (error) {
      console.error("Failed to enter case:", error);
      setEnterCaseError("Unable to open this case. Please try again.");
    } finally {
      setIsEnteringCase(false);
    }
  };

  const openChatModal = (event, user) => {
    event?.stopPropagation();
    setChatData(user);
  };

  return (
    <>
      {loading ? (
        <PulseLoader
          loading={loading}
          size={16}
          aria-label="Loading Spinner"
          data-testid="loader"
          color="#19395F"
          style={{
            display: "flex",
            flexDirection: "row",
            height: "calc(100vh - 119px)",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        />
      ) : (
        <div className="tab-pane fade active show">
          <div className="tab-content table-responsive invisible-scrollbar">
            <Table
              className="table table-borderless table-striped table-treatment has-specialty-icon has-height-25 block-table intake-report-table"
              id="intake-report-table"
            >
              <thead>
                <tr id="tb-header">
                  <th className="intake-report-number" />

                  <th className="text-center">Client Name</th>
                  <th className="intake-report-spacer" />
                  <th className="text-center">Case Type</th>
                  <th className="intake-report-spacer" />
                  <th className="text-center">Created By</th>
                  <th className="intake-report-spacer" />
                  <th className="text-center">Incident</th>
                  <th className="intake-report-spacer" />
                  <th className="text-center">Intake</th>
                  <th className="intake-report-spacer" />
                  <th className="text-center">Retained</th>
                  <th className="intake-report-spacer" />
                  <th className="text-center">Accepted</th>
                  <th className="intake-report-spacer" />
                  <th className="text-center">Dropped</th>
                  <th className="intake-report-spacer" />
                  <th className="text-center">Referred</th>
                  <th className="intake-report-spacer" />
                  <th className="text-center">Rejected</th>
                  <th className="intake-report-spacer" />
                  <th className="text-center">Closed</th>
                  <th
                    style={{
                      minWidth: "10px",
                      width: "10px",
                      maxWidth: "10px",
                    }}
                  />
                </tr>
              </thead>
              <tbody>
                {cases.map((caseData, idx) => (
                  <tr
                    key={`${caseData.client_name ?? "case"}-${caseData.date_open ?? idx}`}
                    className="height-25"
                    onClick={() => {
                      setEnterCaseError("");
                      setSelectedCase(caseData);
                    }}
                  >
                    <td className="intake-report-number text-primary-25 text-center">{idx + 1}</td>
                    <td className="intake-report-client td-autosize">
                      <div className="d-flex align-items-center justify-content-start">
                        <span className="ic m-r-5 ic-avatar ic-19 has-avatar-icon has-cover-img">
                          <img
                            src={caseData.client_avatar || avatarImage}
                            className="output-3"
                            alt={caseData.client_name || "Client"}
                            onError={(event) => {
                              event.currentTarget.src = avatarImage;
                            }}
                          />
                        </span>
                        <span>{renderValue(caseData.client_name)}</span>
                      </div>
                    </td>
                    <td className="intake-report-spacer" />
                    <td className="td-autosize">
                      <div className="d-flex align-items-center justify-content-start">
                        <span className="intake-report-icon-slot m-r-5">
                          {caseData.case_type_icon ? (
                            <img src={caseData.case_type_icon} alt="" />
                          ) : (
                            <span className="text-primary-25" />
                          )}
                        </span>
                        <span>{renderValue(caseData.case_type)}</span>
                      </div>
                    </td>
                    <td className="intake-report-spacer" />
                    <td className="td-autosize">
                      {caseData.created_by ? (
                        <UserChatBubble
                          first_name={caseData.created_by.first_name || ""}
                          last_name={caseData.created_by.last_name || ""}
                          profilePic={caseData.created_by.profile_pic_29p || ""}
                          is_active={caseData.created_by.is_active}
                          isActive={userOnlineStatus?.[caseData.created_by.id]?.isOnline ?? false}
                          width={19}
                          onClick={(event) => openChatModal(event, caseData.created_by)}
                        />
                      ) : (
                        <span className="text-primary-25">-</span>
                      )}
                    </td>
                    <td className="intake-report-spacer" />
                    <td className="td-autosize text-center">
                      {renderDate(caseData, "incident_date")}
                    </td>
                    <td className="intake-report-spacer" />
                    <td className="td-autosize text-center">
                      {renderDate(caseData, "initial_intake_date")}
                    </td>
                    <td className="intake-report-spacer" />
                    <td className="td-autosize text-center">
                      {renderDate(caseData, "retained_date")}
                    </td>
                    <td className="intake-report-spacer" />
                    <td className="td-autosize text-center">
                      {renderDate(caseData, "accepted_date")}
                    </td>
                    <td className="intake-report-spacer" />
                    <td className="td-autosize text-center">
                      {renderDate(caseData, "dropped_date")}
                    </td>
                    <td className="intake-report-spacer" />
                    <td className="td-autosize text-center">
                      {renderDate(caseData, "referred_date")}
                    </td>
                    <td className="intake-report-spacer" />
                    <td className="td-autosize text-center">
                      {renderDate(caseData, "rejected_date")}
                    </td>
                    <td className="intake-report-spacer" />
                    <td className="td-autosize text-center">
                      {renderDate(caseData, "date_closed")}
                    </td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {selectedCase && (
            <EditCaseTypeDateModal
              show
              intakeCase={selectedCase}
              handleClose={() => setSelectedCase(null)}
              onEnterCase={enterCase}
              isEnteringCase={isEnteringCase}
              enterCaseError={enterCaseError}
            />
          )}

          {chatData && (
            <ChatSendingPopup
              show={Boolean(chatData)}
              handleClose={() => setChatData(null)}
              first_name={chatData.first_name || ""}
              last_name={chatData.last_name || ""}
              userId={chatData.id}
              is_active={chatData.is_active}
              profilePic={chatData.profile_pic_29p || ""}
            />
          )}
        </div>
      )}
    </>
  );
};

export default IntakeReporting;
